import React, { useEffect, useState } from 'react';
import { notificar } from '../../components/SistemaToast/notificar';
import DateInput from '../../components/DateInput/DateInput';
import { urlApiProd } from '../utiles';
import AsignarHc from './AsignarHc';
import TarjetaPiSinBooking from './TarjetaPiSinBooking';

// Contenido de cada alerta con panel: reglas acordadas de encendido, apagado y destinatario.
const ALERTAS = {
    pi_sin_booking: {
        eyebrow: 'Alerta · Planificación',
        titulo: 'PI sin booking',
        descripcion: 'PI con saldo sin booking cuyo ETD objetivo se acerca y que todavía no tienen booking pedido. Se separan según quién debe moverse: Comercial (falta solicitud) o Logística (solicitud sin booking).',
        enciende: 'Saldo > 0 y ETD objetivo en 21 días o menos',
        apaga: 'Cuando todo el saldo tiene booking solicitado',
        avisa: 'Comercial o Logística, según el caso',
        pie: 'Plazo: ETD objetivo en 21 días o menos (incluye vencidos).',
    },
    hc_sin_asignar: {
        eyebrow: 'Alerta · Asignación',
        titulo: 'HC sin asignar',
        descripcion: 'Bookings confirmados con contenedores reservados que aún no tienen PI, OC ni proveedor. Sin asignación no se puede coordinar la coleta.',
        enciende: 'Booking confirmado con HC libres y DLD en 7 días o menos',
        apaga: 'Asignados = capacidad, o se reduce el booking',
        avisa: 'Comercial',
        pie: 'Las PI sugeridas tienen el mismo destino, saldo sin booking y OC vinculada.',
    },
    coleta: {
        eyebrow: 'Alerta · Plazo',
        titulo: 'Coleta (DLD)',
        descripcion: 'Bookings cuyo plazo para retirar los contenedores vacíos (DLD) está encima y la coleta aún no está agendada con el proveedor.',
        enciende: 'DLD en 5 días o menos y coleta “Sin agendar”',
        apaga: 'Al registrar la fecha de coleta agendada',
        avisa: 'Logística responsable del booking',
        pie: 'Plazo: DLD en 5 días o menos (incluye vencidas).',
        genero: 'a', // "Vencida"
    },
    dlc: {
        eyebrow: 'Alerta · Plazo',
        titulo: 'Cut-off de carga (DLC)',
        descripcion: 'Bookings cuyo plazo para entregar los contenedores llenos en el puerto (DLC) está encima y todavía hay HC fuera del puerto.',
        enciende: 'DLC en 3 días o menos y HC en puerto menor al total',
        apaga: 'Cuando todos los HC tienen Gate In',
        avisa: 'Logística y forwarder (opcional)',
        pie: 'Plazo: DLC en 3 días o menos (incluye vencidos).',
        genero: 'o', // "Vencido"
    },
};

const fmtDiaMes = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');

const etiquetaPlazo = (dias, genero) => {
    if (dias < 0) return `Vencid${genero} hace ${-dias} ${-dias === 1 ? 'día' : 'días'}`;
    if (dias === 0) return 'Vence hoy';
    return `Faltan ${dias} ${dias === 1 ? 'día' : 'días'}`;
};

const PanelAlerta = ({ tipo, items, onCerrar, onFicha, onRegistrarColeta, onAsignarHc, onCrearSolicitud, onSolicitarBooking }) => {
    const config = ALERTAS[tipo];
    // "PI sin booking": filtro por responsable (todas | comercial | logistica)
    const [filtro, setFiltro] = useState('todas');
    const cuentaComercial = items.filter((i) => i.comercial).length;
    const cuentaLogistica = items.filter((i) => i.logistica).length;
    const visibles = tipo === 'pi_sin_booking' && filtro !== 'todas'
        ? items.filter((i) => i[filtro])
        : items;
    const [fechas, setFechas] = useState({});
    const [contenedores, setContenedores] = useState({});
    const [guardando, setGuardando] = useState(null);
    const [error, setError] = useState('');
    // PI con saldo para sugerir en "HC sin asignar" (null = cargando)
    const [pis, setPis] = useState(null);
    const [versionPis, setVersionPis] = useState(0);

    useEffect(() => {
        if (tipo !== 'hc_sin_asignar') return undefined;
        let activo = true;
        const cargar = async () => {
            try {
                const token = localStorage.getItem('token');
                const respuesta = await fetch(`${urlApiProd}/controlembarques/pi-asignables`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!respuesta.ok) throw new Error('No se pudieron cargar las PI sugeridas');
                const datos = await respuesta.json();
                if (activo) setPis(datos);
            } catch (e) {
                if (activo) {
                    setPis([]);
                    notificar.error(e.message);
                }
            }
        };
        cargar();
        return () => { activo = false; };
    }, [tipo, versionPis]);

    // Asigna y recarga las PI sugeridas, porque su saldo cambió
    const asignarHc = async (idSolicitud, datos) => {
        await onAsignarHc(idSolicitud, datos);
        setVersionPis((v) => v + 1);
    };

    useEffect(() => {
        const alPresionar = (e) => { if (e.key === 'Escape') onCerrar(); };
        document.addEventListener('keydown', alPresionar);
        return () => document.removeEventListener('keydown', alPresionar);
    }, [onCerrar]);

    // Registra la coleta; el nombre del contenedor es opcional
    const registrar = async (item) => {
        setError('');
        if (!fechas[item.id_solicitud]) {
            notificar.aviso('Ingresa la fecha de coleta para registrarla.', { toastId: `coleta-sin-fecha-${item.id_solicitud}` });
            return;
        }
        setGuardando(item.id_solicitud);
        try {
            await onRegistrarColeta(item.id_solicitud, fechas[item.id_solicitud], contenedores[item.id_solicitud] || '');
            notificar.exito(`Coleta registrada del booking ${item.nro_booking || `Sin N° (${item.ffw || '—'})`}.`);
        } catch (e) {
            setError(e.message);
        } finally {
            setGuardando(null);
        }
    };

    return (
        <div className="ap-overlay" onClick={onCerrar}>
            <aside
                className="ap-panel"
                role="dialog"
                aria-label={config.titulo}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="ap-cabecera">
                    <div className="d-flex justify-content-between align-items-start">
                        <div className="ui-eyebrow">{config.eyebrow}</div>
                        <button type="button" className="ap-cerrar" onClick={onCerrar} aria-label="Cerrar panel">✕</button>
                    </div>
                    <h4 className="ui-title mb-2">
                        {config.titulo} <span className="ap-contador">{items.length}</span>
                    </h4>
                    <p className="ui-descripcion">{config.descripcion}</p>
                    <div className="ap-reglas">
                        <div><strong>Se enciende</strong><span>{config.enciende}</span></div>
                        <div><strong>Se apaga</strong><span>{config.apaga}</span></div>
                        <div><strong>Avisa a</strong><span>{config.avisa}</span></div>
                    </div>
                </div>

                <div className="ap-cuerpo">
                    {tipo === 'pi_sin_booking' && (
                        <div className="ce-chips">
                            {[
                                { id: 'todas', texto: `Todas ${items.length}` },
                                { id: 'comercial', texto: `Falta solicitud (Comercial) ${cuentaComercial}` },
                                { id: 'logistica', texto: `Solicitud sin booking (Logística) ${cuentaLogistica}` },
                            ].map((chip) => (
                                <button
                                    key={chip.id}
                                    type="button"
                                    className={`ce-chip${filtro === chip.id ? ' active' : ''}`}
                                    onClick={() => setFiltro(chip.id)}
                                >
                                    {chip.texto}
                                </button>
                            ))}
                        </div>
                    )}

                    {visibles.length === 0 && (
                        <div className="ce-muted text-center py-4">
                            {tipo === 'pi_sin_booking' ? 'No hay PI con esta alerta.' : 'No hay bookings con esta alerta.'}
                        </div>
                    )}

                    {tipo === 'pi_sin_booking' && visibles.map((item) => (
                        <TarjetaPiSinBooking
                            key={item.id_proforma}
                            item={item}
                            onCrearSolicitud={onCrearSolicitud}
                            onSolicitarBooking={onSolicitarBooking}
                        />
                    ))}

                    {tipo !== 'pi_sin_booking' && items.map((item) => (
                        <div key={item.id_solicitud} className="ap-card">
                            <div className="d-flex justify-content-between align-items-center gap-2">
                                <span className="ce-mono fw-bold">
                                    {item.nro_booking || `Sin N° (${item.ffw || '—'})`}
                                </span>
                                <span className={`ce-pill ${item.dias <= (tipo === 'hc_sin_asignar' ? 3 : 0) ? 'ce-pill--sin' : 'ce-pill--solicitado'}`}>
                                    {tipo === 'hc_sin_asignar' ? `${item.libres} HC libres` : etiquetaPlazo(item.dias, config.genero)}
                                </span>
                            </div>
                            <div className="fw-semibold my-1">
                                {[item.ffw, item.carrier].filter(Boolean).join(' · ') || '—'} · {item.pol || '—'} → {item.pod || '—'}
                            </div>

                            {tipo === 'hc_sin_asignar' ? (
                                <div className="ap-datos">
                                    <div><span>Asignados</span><strong className="ce-mono">{item.asignados} de {item.hc}</strong></div>
                                    <div><span>DLD</span><strong className="ce-mono">{fmtDiaMes(item.dld)}</strong></div>
                                    <div><span>ETD</span><strong className="ce-mono">{fmtDiaMes(item.etd)}</strong></div>
                                    <div><span>Cliente</span><strong>{item.clientes.length > 0 ? item.clientes.join(', ') : 'Sin definir'}</strong></div>
                                </div>
                            ) : tipo === 'coleta' ? (
                                // DLD y ETD son del booking (grandes, una sola vez); el HC se muestra por proveedor, en filas
                                <>
                                    <div className="ap-fechas">
                                        <div><span>DLD</span><strong className="ce-mono">{fmtDiaMes(item.dld)}</strong></div>
                                        <div><span>ETD</span><strong className="ce-mono">{fmtDiaMes(item.etd)}</strong></div>
                                    </div>
                                    <div className="ap-filas">
                                        <div className="ap-fila ap-fila-cabecera">
                                            <span>Proveedor</span><span>HC</span>
                                        </div>
                                        {(item.proveedores_hc.length > 0
                                            ? item.proveedores_hc
                                            : [{ id_proveedor: 0, proveedor: 'Sin asignar', hc: item.hc }]
                                        ).map((p) => (
                                            <div key={p.id_proveedor} className="ap-fila">
                                                <strong className="ap-recorte">{p.proveedor}</strong>
                                                <strong className="ce-mono">{p.hc}</strong>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="ap-datos">
                                    <div><span>DLC</span><strong className="ce-mono">{fmtDiaMes(item.dlc)}</strong></div>
                                    <div><span>ETD</span><strong className="ce-mono">{fmtDiaMes(item.etd)}</strong></div>
                                    <div><span>En puerto</span><strong className="ce-mono">{item.en_puerto} de {item.hc}</strong></div>
                                    <div><span>Nave</span><strong>{item.nave || 'Por confirmar'}</strong></div>
                                </div>
                            )}

                            {tipo === 'hc_sin_asignar' ? (
                                pis === null ? (
                                    <div className="ap-sin-sugerencia">Cargando PI sugeridas…</div>
                                ) : (
                                    <AsignarHc item={item} pis={pis} onAsignar={asignarHc} onFicha={onFicha} />
                                )
                            ) : (
                            <div className="ap-accion">
                                {tipo === 'coleta' ? (
                                    <>
                                        <label className="ce-filtro flex-grow-1">
                                            Fecha de coleta
                                            <DateInput
                                                name={`coleta-${item.id_solicitud}`}
                                                value={fechas[item.id_solicitud] || ''}
                                                onChange={(e) => setFechas((actual) => ({ ...actual, [item.id_solicitud]: e.target.value }))}
                                            />
                                        </label>
                                        <label className="ce-filtro flex-grow-1">
                                            Nombre de contenedor
                                            <input
                                                type="text"
                                                maxLength={255}
                                                placeholder="Opcional"
                                                value={contenedores[item.id_solicitud] || ''}
                                                onChange={(e) => setContenedores((actual) => ({ ...actual, [item.id_solicitud]: e.target.value }))}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="ui-btn ui-btn-primary"
                                            disabled={guardando === item.id_solicitud}
                                            onClick={() => registrar(item)}
                                        >
                                            {guardando === item.id_solicitud ? 'Guardando…' : 'Registrar'}
                                        </button>
                                    </>
                                ) : (
                                    <span className="ce-muted flex-grow-1">El Gate In se registra por contenedor en la ficha.</span>
                                )}
                                <button type="button" className="ce-accion" onClick={() => onFicha(item.id_solicitud)}>Ficha</button>
                            </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="ap-pie">
                    {error ? <span className="error">{error}</span> : <span className="ce-muted">{config.pie}</span>}
                </div>
            </aside>
        </div>
    );
};

export default PanelAlerta;
